// features/workforce/mockData/workforceData.ts
// Single source of truth for ALL Workforce & Cooperatives mock data.

// ─── Tab definitions ──────────────────────────────────────────────────────────
// Two rows of tabs as seen in images: primary row + secondary row
export const PRIMARY_TABS = [
  { key: "actors",      label: "Livestock Actors"             },
  { key: "qual",        label: "Actors Qualification"         },
  { key: "deployment",  label: "Workforce Deployment Records" },
  { key: "farmer",      label: "Farmer Producer Records"      },
  { key: "coop",        label: "Cooperatives & Organization"  },
];

export const SECONDARY_TABS = [
  { key: "insurance", label: "Insurance Records – NAIC"           },
  { key: "finance",   label: "Finance Access Records – CBN/NIRSAL/BOA" },
];

export const ALL_TABS = [...PRIMARY_TABS, ...SECONDARY_TABS];

// ─── Filter options ───────────────────────────────────────────────────────────
export const filterOptions = {
  states:       ["Kano", "FCT", "Abia", "Adamawa", "Benue", "Kaduna", "Kwara", "Kogi", "Osun"],
  lgas:         ["All", "Kano Municipal", "Garko", "Kabba", "Bebeji"],
  marketTypes:  ["All", "Livestock Market", "Agro-Input", "Wholesale"],
  statuses:     ["All", "Active", "Inactive", "Suspended", "Pending"],
  actorTypes:   [
    "All Actor Types", "Veterinarian", "Animal Scientist", "Extension Worker",
    "Livestock Inspector", "Abattoir Officer", "Vet Paraprofessional",
    "CAHW", "Cooperative Officer", "Trader", "Farmer Producer",
    "Processor", "Transporter", "Input Supplier", "Researcher", "Other",
  ],
};

// ─── TAB 1: Livestock Actors ──────────────────────────────────────────────────
export const actorStats = [
  { label: "Total Registered Actors",    value: "6.7k", sub: "Total number of Actors registered"                                        },
  { label: "Active vs. Inactive",        value: "4.5k", sub: "Total number of active registered actors"                                  },
  { label: "Veterinarians",              value: "2.4k", sub: "Total number of all active registered Veterinarians"                       },
  { label: "Animal Scientist",           value: "4.5k", sub: "Total number of all active registered Animal Scientist"                    },
  { label: "Extension Officers & CAHWs", value: "6.4K", sub: "Total number of all active registered Extension Officers and CAHWs"        },
  { label: "Farmer / Producers",         value: "6.7k", sub: "Total number of all active registered Farmer / Producers"                  },
];

const actorTypeOptions = ["Veterinarian","Animal Scientist","Extension Worker","Livestock Inspector","Abattoir Officer","CAHW","Cooperative Officer","Farmer Producer","Input Supplier"];
const sexOptions        = ["Male","Female"];
const ageGroups         = ["18 to 25","26 to 35","36 to 45","46 to 55","56+"];
const statusOpts        = ["Active","Inactive","Suspended"];
const verifyOpts        = ["Pending","Rejected","Verified"];

export const actorRecords = Array.from({ length: 20 }, (_, i) => ({
  id:                 `A${i + 1}`,
  fullName:           "Full name",
  actorType:          actorTypeOptions[i % actorTypeOptions.length],
  sex:                sexOptions[i % 2],
  ageGroup:           ageGroups[i % ageGroups.length],
  status:             statusOpts[i % statusOpts.length],
  verificationStatus: verifyOpts[i % verifyOpts.length],
}));

// ─── TAB 2: Actors Qualification ─────────────────────────────────────────────
const qualTypes = [
  "Degree Vet Medicine","Degree Animal Science","Diploma","Certificate",
  "Professional License","Short Course","Extension Training","CPD",
];
const verifyStatuses = ["Pending","Rejected","Verified"];

export const qualRecords = Array.from({ length: 20 }, (_, i) => ({
  id:                 `Q${i + 1}`,
  fullName:           "Full name",
  qualificationType:  qualTypes[i % qualTypes.length],
  institution:        "ABU",
  yearAwarded:        2022,
  specialization:     "Diagnostic Centre",
  expiryRenewal:      "05-2026/05-2028",
  verificationStatus: verifyStatuses[i % verifyStatuses.length],
}));

// ─── TAB 3: Workforce Deployment ─────────────────────────────────────────────
export const deploymentStats = [
  { label: "Total Active Deployments",      value: "2,550", sub: "Total Active Deployments",         icon: "doc",   color: "black" },
  { label: "Undeployed Registered Actors",  value: "187",   sub: "Undeployed Registered Actors",     icon: "doc",   color: "black" },
  { label: "States with No Vet Deployment", value: "12",    sub: "States with No Vet Deployment",    icon: "pct",   color: "amber" },
  { label: "Expiring Postings (30 days)",   value: "Day 28",sub: "Expiring Postings (30 days)",      icon: "clock", color: "green" },
];

const deployStatuses   = ["Active","Inactive","Verified"];
const deployTypes      = ["Permanent","Temporary","Field Mission","Training Attachment","Emergency"];
const mobilityStatuses = ["Has Vehicle","Shared Vehicle","No Vehicle"];
const qualTypesD       = ["Degree Vet Medicine","Degree Animal Science","Diploma","Certificate","Professional License","Short Course"];

export const deploymentRecords = Array.from({ length: 20 }, (_, i) => ({
  id:               `D${i + 1}`,
  actorName:        "Actor Name",
  actorType:        qualTypesD[i % qualTypesD.length],
  status:           deployStatuses[i % deployStatuses.length],
  postingInstitution:"ABU",
  state:            "Kogi",
  lga:              "Kabba",
  startDate:        "14-04-2026",
  endDate:          "19-05-2028",
  supervisor:       "Abiodun Gabriel",
  deploymentType:   deployTypes[i % deployTypes.length],
  mobilityStatus:   mobilityStatuses[i % mobilityStatuses.length],
}));

// ─── TAB 4: Farmer Producer Records ──────────────────────────────────────────
export const farmerStats = [
  {
    label: "Total Registered Farmers", value: "6.7k",
    sub: "(5.6k) 20% lesser last month ↗\nTrend (Green) vs. last month (Gray)", color: "green",
  },
  {
    label: "Extension Officer Assigned Rate", value: "85%",
    sub: "% farmers with assigned extension officer\nGreen ≥70% / Amber 50–69% / Red <50%", color: "green",
  },
  {
    label: "Farmers with Herd Reg Number", value: "60%",
    sub: "% linked to Population module\nGreen ≥80% / Amber 60–79% / Red <60%", color: "amber",
  },
];

export const farmerProductionSystemChart = [
  { label: "Pastoral System",     pct: 55, color: "#1a237e" },
  { label: "Agropastoral System", pct: 12, color: "#ff1744" },
  { label: "Commercial System",   pct: 25, color: "#7b1fa2" },
  { label: "Backyard",            pct: 8,  color: "#4caf50" },
  { label: "Extensive",           pct: 5,  color: "#f57c00" },
];

const productionSystems = ["Pastoral","Agropastoral","Commercial","Backyard","Nomadic","Extensive"];
const speciesOpts       = ["Cattle","Goat","Sheep","Chicken","Bee"];
const herdClasses       = ["Large","Medium","Small","Micro"];
const farmerStatuses    = ["Active","Inactive"];

export const farmerRecords = Array.from({ length: 20 }, (_, i) => ({
  id:                    `F${i + 1}`,
  farmerName:            "Farmer Name",
  productionSystem:      productionSystems[i % productionSystems.length],
  species:               speciesOpts[i % speciesOpts.length],
  herdSizeClass:         herdClasses[i % herdClasses.length],
  farmSize:              1450,
  state:                 "Kaduna",
  startDate:             "19-05-2028",
  status:                farmerStatuses[i % farmerStatuses.length],
  farmHerdRegNo:         "No 12.J1",
  primaryMarketRef:      "Primary Market Reference",
  extensionOfficer:      "Extension Officer",
}));

// ─── TAB 5: Cooperatives & Organization ──────────────────────────────────────
export const coopStats = [
  { label: "Total Registered Organisations", value: "6.7k",  sub: "(5.6k) 20% lesser last month ↗\nTrend (Green) vs. last month (Red)", color: "green" },
  { label: "Active Organisations",           value: "93%",   sub: "Green ≥80% / Amber 60–79% / Red <60%",                               color: "green" },
  { label: "Total Active Membership",        value: "95%",   sub: "Green ≥95% / Amber 85–94% / Red <85%",                               color: "green" },
  { label: "Organisations with Bank Account",value: "93%",   sub: "Green ≥70% / Amber 50–69% / Red <50%",                               color: "green" },
];

const regAuthority = ["CAC","FMLD","State Ministry","Cooperative Society","Other"];
const bankStatuses = ["Active","Inactive"];

export const coopRecords = Array.from({ length: 20 }, (_, i) => ({
  id:                  `CO${i + 1}`,
  orgName:             "Organisation Name",
  regNumber:           "No 12.J1",
  state:               "Kaduna",
  totalMembers:        6000,
  activeMembers:       5800,
  registeringAuthority:regAuthority[i % regAuthority.length],
  dateOfRegistration:  "19-05-2028",
  bankAccountStatus:   bankStatuses[i % bankStatuses.length],
  primaryActivities:   "Primary Activities",
}));

// ─── TAB 6: Insurance Records – NAIC ─────────────────────────────────────────
// Card-based layout (2 columns), grouped by actor, with pagination per page
const subsidySources    = ["Government Subsidised","Commercial","Hybrid"];
const naicStatuses      = ["Active","Inactive","Under Review","Lapsed"];
const premiumAmounts    = ["₦150,000","₦300,000","₦650,000","₦450,000"];

export const insuranceRecords = Array.from({ length: 12 }, (_, i) => ({
  id:                `INS${i + 1}`,
  actorId:           "Actor ID",
  farmReference:     "Farm Reference",
  insurer:           "Insurer",
  policyNumber:      "Policy Number",
  policyType:        "Policy Type",
  speciesCovered:    "Species Covered",
  animalsInsuredCount:7668,
  coverageStart:     2024,
  coverageEnd:       2028,
  premiumAmount:     premiumAmounts[i % premiumAmounts.length],
  subsidySource:     subsidySources[i % subsidySources.length],
  naicSyncStatus:    "Green",
  status:            naicStatuses[i % naicStatuses.length],
}));

// ─── TAB 7: Finance Access Records – CBN/NIRSAL/BOA ─────────────────────────
// Accordion per institution, 2-column card grid inside, pagination per accordion
const repaymentStatuses  = ["Completed","Overdue","Defaulted","Current","Restructured"];
const collateralTypes    = ["Livestock","Land","Guarantee","None"];

export const financeGroups = {
  CBN: Array.from({ length: 8 }, (_, i) => ({
    id:               `CBN${i + 1}`,
    institution:      "CBN",
    actorId:          "CBN",
    collateralType:   collateralTypes[i % collateralTypes.length],
    repaymentStatus:  repaymentStatuses[i % repaymentStatuses.length],
  })),
  NIRSAL: Array.from({ length: 6 }, (_, i) => ({
    id:               `NIR${i + 1}`,
    institution:      "NIRSAL",
    actorId:          "NIRSAL",
    collateralType:   collateralTypes[i % collateralTypes.length],
    repaymentStatus:  repaymentStatuses[i % repaymentStatuses.length],
  })),
  BOA: Array.from({ length: 6 }, (_, i) => ({
    id:               `BOA${i + 1}`,
    institution:      "BOA",
    actorId:          "BOA",
    collateralType:   collateralTypes[i % collateralTypes.length],
    repaymentStatus:  repaymentStatuses[i % repaymentStatuses.length],
  })),
  "Commercial Bank": Array.from({ length: 4 }, (_, i) => ({
    id:               `CB${i + 1}`,
    institution:      "Commercial Bank",
    actorId:          "Commercial Bank",
    collateralType:   collateralTypes[i % collateralTypes.length],
    repaymentStatus:  repaymentStatuses[i % repaymentStatuses.length],
  })),
};
